import React, { useMemo } from 'react';
import { Avatar, Dropdown } from 'antd';
import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { NavLink } from 'react-router-dom';
import { PROFILE_PATH } from '../../../constants/Constant';
import { useAuth } from '../../../context/AuthContext';
import { getUserInitials } from '../../../utils/AuthUtils';
import OpterraPhoenixLogo from '../../../assets/images/OpterraPhoenixLogo.png';
import MergeClassNames from '../../../utils/MergeClassNames';

interface IHeaderProps {
  onMenuToggle?: () => void;
  /**
   * Drop the white fill behind the desktop user menu — for pages whose top
   * edge isn't white (e.g. LimeMate's grey surface), where it would read as
   * a stray box.
   */
  transparentOnDesktop?: boolean;
}

const Header: React.FC<IHeaderProps> = ({
  onMenuToggle,
  transparentOnDesktop = false,
}) => {
  const { user, logout } = useAuth();

  const userInitials = useMemo(() => getUserInitials(user), [user]);

  const userDropdownMenu = {
    items: [
      {
        key: 'profile',
        label: (
          <NavLink to={PROFILE_PATH} className="flex items-center text-base">
            <UserOutlined className="mr-2" />
            <span>Profile</span>
          </NavLink>
        ),
      },
      {
        key: 'logout',
        label: (
          <div
            className="flex items-center px-3 py-1 text-base"
            onClick={logout}
          >
            <LogoutOutlined className="mr-2" />
            <span>Logout</span>
          </div>
        ),
        style: { padding: 0 },
      },
    ],
  };

  return (
    <header
      className={MergeClassNames(
        'fixed top-0 z-[60] flex h-16 items-center justify-between bg-gray-bright px-4 py-3 md:right-0 md:mr-1 md:w-fit md:bg-white md:px-6',
        'w-full',
        transparentOnDesktop && 'md:bg-transparent'
      )}
    >
      <div className="flex items-center gap-4 md:hidden">
        <img
          src={OpterraPhoenixLogo}
          alt="Opterra Logo"
          className="h-8 w-auto"
        />
        <button
          onClick={onMenuToggle}
          className="flex items-center text-2xl text-gray-dark"
        >
          <MenuOutlined />
        </button>
      </div>

      <div className="ml-auto flex items-center border-l-2 border-gray-argent py-1 pl-3 md:ml-0">
        <Dropdown
          menu={userDropdownMenu}
          placement="bottomRight"
          trigger={['click']}
        >
          <Avatar className="cursor-pointer bg-gray-dark" size="large">
            {userInitials || <UserOutlined />}
          </Avatar>
        </Dropdown>
      </div>
    </header>
  );
};

export default Header;
